import React, { FC, useEffect, useRef, useState } from 'react';
import { useTrainingInitLazyQuery } from '~/data';
import { select } from '~/lib/select';
import {
  fetchLearnerLanguagePreference,
  getLmsAdapter,
  logLmsLanguage
} from '~/lms';
import { NotFound } from '~/ui/not-found/NotFound';
import { Spinner } from '~/ui/spinner/Spinner';
import { useTrainingParams } from '../useTrainingParams';
import { processData } from './processData';
import { TrainingState } from './TrainingState';
import { useTrainingStore } from './useTrainingStore';

const selector = (s: TrainingState) =>
  select(s, 'textLocale', 'mediumLocale', 'subtitleLocale');

export const InitTrainingState: FC = ({ children }) => {
  const { learnerTrainingId } = useTrainingParams();
  const [getTrainingInit, { data, refetch, error }] =
    useTrainingInitLazyQuery();
  const [done, setDone] = useState(false);
  const { textLocale, mediumLocale, subtitleLocale } =
    useTrainingStore(selector);

  useEffect(() => {
    // This will automatically call the API back when the language is changed,
    // ensuring that the navigation menu is updated.
    //
    // Skip while on onboarding: LMS/browser auto-detect updates `textLocale`
    // locally before the server catches up — refetching here would reload
    // stale `en` from trainingInit and reset the language picker.
    const isOnboarding =
      window.location.pathname.split('/').pop() === 'onboarding';

    if (refetch && data?.learnerTraining && !isOnboarding) {
      if (
        data.learnerTraining.textLocale != textLocale ||
        data.learnerTraining.mediumLocale != mediumLocale ||
        data.learnerTraining.subtitleLocale != subtitleLocale
      ) {
        document.location.reload();
      }

      if (
        data.learnerTraining.textLocale != textLocale ||
        data.learnerTraining.mediumLocale != mediumLocale ||
        data.learnerTraining.subtitleLocale != subtitleLocale
      ) {
        refetch();
      }
    }
  }, [textLocale, mediumLocale, subtitleLocale]);

  // Call the API when the learner training id is present or updated
  useEffect(() => {
    if (!learnerTrainingId) return;
    getTrainingInit({ variables: { learnerTrainingUuid: learnerTrainingId } });
  }, [learnerTrainingId]);

  // Process the API data and update the store when data changes
  useEffect(() => {
    let unloaded = false;
    if (!data) return;
    useTrainingStore.setState(processData(data));

    const isOnboarding =
      window.location.pathname.split('/').pop() === 'onboarding';

    // Fire-and-forget: start the SCORM read early without blocking the spinner.
    // LargeLocaleSelector awaits the same single-flight promise on mount.
    if (isOnboarding) {
      try {
        const lms = getLmsAdapter();
        logLmsLanguage('init-training warm-up', {
          adapter: lms.name,
          isAvailable: lms.isAvailable
        });
        if (lms.isAvailable) {
          void fetchLearnerLanguagePreference(lms);
        }
      } catch (error) {
        logLmsLanguage('init-training warm-up failed', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    const timer = setTimeout(() => {
      if (!unloaded) setDone(true);
    }, 0);
    return () => {
      unloaded = true;
      clearTimeout(timer);
    };
  }, [data]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statusCode = (error?.networkError as any)?.statusCode;
  if (statusCode === 401 || statusCode === 404) return <NotFound />;

  if (!done) return <Spinner />;

  if (!children) return null;

  return <>{children}</>;
};
